import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

const CORE_SIZE = 8;

const pulseCore = keyframes`
  0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
  50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.9; }
`;

const pulseHalo = keyframes`
  0% { transform: translate(-50%, -50%) scale(0.7); opacity: 0.78; }
  75% { transform: translate(-50%, -50%) scale(2.1); opacity: 0.15; }
  100% { transform: translate(-50%, -50%) scale(2.1); opacity: 0; }
`;

const DotRoot = styled.span`
  position: absolute;
  top: ${(props) => props.$top ?? -5}px;
  left: ${(props) => props.$left ?? -5}px;
  width: ${(props) => props.$size ?? CORE_SIZE}px;
  height: ${(props) => props.$size ?? CORE_SIZE}px;
  pointer-events: none;
  z-index: 2;
`;

const Halo = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: rgba(255, 59, 48, 0.58);
  transform: translate(-50%, -50%) scale(0.7);
  animation: ${pulseHalo} 2.4s cubic-bezier(0.22, 1, 0.36, 1) infinite;
`;

const Core = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${CORE_SIZE}px;
  height: ${CORE_SIZE}px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #ff6961 0%, #ff3b30 55%, #e6352b 100%);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.12),
    0 0 10px rgba(255, 59, 48, 0.55);
  transform: translate(-50%, -50%) scale(1);
  animation: ${pulseCore} 2.4s ease-in-out infinite;
`;

const Wrapper = styled.div`
  position: relative;
  display: ${(props) => (props.$inline ? 'inline-flex' : 'block')};
  width: ${(props) => (props.$fill ? '100%' : 'auto')};
`;

function DotMark({ size, top, left }) {
  return (
    <DotRoot $size={size} $top={top} $left={left} aria-hidden>
      <Halo />
      <Core />
    </DotRoot>
  );
}

/**
 * iOS-style unread indicator for completed AI evaluations.
 */
export default function AiUnreadDot({
  visible = true,
  children,
  inline = false,
  fill = false,
  size,
  top,
  left,
}) {
  if (!children) {
    return visible ? <DotMark size={size} top={top} left={left} /> : null;
  }

  return (
    <Wrapper $inline={inline} $fill={fill}>
      {children}
      {visible && <DotMark size={size} top={top} left={left} />}
    </Wrapper>
  );
}
